import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Loader2, CheckCircle } from "lucide-react";
import hospitalService from "../services/hospitalService";
import bookingService from "../services/bookingService";
import authService from "../services/authService";
import apiService from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { getAmbulanceTypeAbbreviation } from "../utils/ambulanceTypeHelper";
import ambulanceImage1 from "../svg/Rectangle.png";
import ambulanceImage2 from "../svg/Rectangle-2.png";
import ambulanceImage3 from "../svg/Rectangle-3.png";
import ambulanceImage4 from "../svg/Rectangle-4.png";
import ambulanceImage5 from "../svg/Rectangle-5.png";
import ambulanceImage6 from "../svg/Rectangle-6.png";

// Array of ambulance images
const ambulanceImages = [ambulanceImage1, ambulanceImage2, ambulanceImage3, ambulanceImage4, ambulanceImage5, ambulanceImage6];

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Dropdown button component for payment selection
const PaymentDropdownButton = ({ value, label, icon, currentPaymentType, onSelect, onClose }) => (
  <button
    type="button"
    className={`w-full px-4 py-3 text-sm text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${
      currentPaymentType === value
        ? "bg-blue-50 text-blue-700"
        : "text-gray-700"
    }`}
    onClick={() => {
      onSelect(value);
      onClose();
    }}
  >
    <span className="flex items-center gap-2">{icon} {label}</span>
    {currentPaymentType === value && (
      <CheckCircle className="w-4 h-4 text-blue-600" />
    )}
  </button>
);

// Helper function to check if booking button should be disabled
const isBookingDisabled = (isLoading, bookingFormData, selectedAmbulance, pickupCoordinates, dropCoordinates) => {
  if (!bookingFormData) return true;
  return isLoading ||
    !bookingFormData.name?.trim() ||
    (!bookingFormData.phone?.trim() && !bookingFormData.email?.trim()) ||
    !pickupCoordinates ||
    !dropCoordinates ||
    (bookingFormData.bookingType === "scheduled" && (!bookingFormData.date || !bookingFormData.time)) ||
    !selectedAmbulance;
};

const ChooseAmbulance = ({
  onAmbulanceSelect,
  selectedAmbulance,
  onConfirmSelection,
  pickupCoordinates,
  dropCoordinates,
  onFareEstimatesChange,
  journeyDirection,
  bookingFormData,
  triggerBooking,
}) => {
  const { user } = useAuth();

  // Helper: Check if user is a hospital user
  const isHospitalUser = useMemo(() => {
    return user?.role === 'HOSPITAL_USER' ||
      user?.role === 'hospital' ||
      user?.role === 'Hospital';
  }, [user?.role]);

  // Helper: Check if user is a fleet user
  const isFleetUser = useMemo(() => {
    return user?.role === 'TENANT_ADMIN' ||
      user?.role === 'tenant_admin' ||
      user?.role === 'Fleet';
  }, [user?.role]);
  const [ambulanceTypes, setAmbulanceTypes] = useState([]);
  const [allAmbulanceTypes, setAllAmbulanceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fareEstimates, setFareEstimates] = useState({});
  const [fareLoading, setFareLoading] = useState({});
  const [calculatedDistance, setCalculatedDistance] = useState(null); // No default - will be calculated from backend
  const [isFiltered, setIsFiltered] = useState(false); // Track if filtering is active
  const [filterInfo, setFilterInfo] = useState(null); // Store filter information for debugging
  const [fleetPartnerNames, setFleetPartnerNames] = useState([]); // Initialize empty, populated from API response
  const [fleetOperatorMap, setFleetOperatorMap] = useState({}); // Initialize with empty map
  const [partnerTypeIds, setPartnerTypeIds] = useState(new Set()); // Track which type_ids belong to partner fleets
  const [vendorNamesMap, setVendorNamesMap] = useState({}); // Map type_id to vendor name from estimate fare API

  // Payment state
  const [paymentType, setPaymentType] = useState("Cash");
  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const paymentDropdownRef = useRef(null);

  // Pricing breakdown overlay state
  const [pricingInfoData, setPricingInfoData] = useState(null);

  // Use ref instead of state for fareCalculationKey to avoid re-render loops
  const fareCalculationKeyRef = useRef(null);
  const renderCountRef = useRef(0);

  // Initialize payment type from bookingFormData
  useEffect(() => {
    if (bookingFormData?.paymentType) {
      setPaymentType(bookingFormData.paymentType);
    }
  }, [bookingFormData?.paymentType]);

  // Handle clicks outside payment dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        paymentDropdownRef.current &&
        !paymentDropdownRef.current.contains(event.target)
      ) {
        setIsPaymentDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debug: track renders
  useEffect(() => {
    renderCountRef.current += 1;
    if (renderCountRef.current % 10 === 0) {
      console.warn(`🔄 ChooseAmbulancecard has re-rendered ${renderCountRef.current} times`);
    }
  });

  // Helper function to get fallback ambulance types
  const getFallbackAmbulanceTypes = (prefix = "fallback") => [
    {
      type_id: `${prefix}-1`,
      name: "Basic Life Support",
      description: "Standard ambulance service",
      base_fare: 500,
      per_km_rate: 15
    },
    {
      type_id: `${prefix}-2`,
      name: "Advanced Life Support",
      description: "Advanced medical equipment",
      base_fare: 800,
      per_km_rate: 20
    }
  ];

  // Helper function to apply fallback types
  const applyFallbackTypes = (prefix = "fallback") => {
    const fallbackTypes = getFallbackAmbulanceTypes(prefix);
    setAllAmbulanceTypes(fallbackTypes);
    setAmbulanceTypes(fallbackTypes);
  };

  // Helper function to select API based on user role
  const selectAmbulanceTypesAPI = async () => {
    if (isFleetUser) {
      console.log("🚛 Loading ambulance types for fleet user");
      return await authService.getAmbulanceTypes();
    }
    if (isHospitalUser) {
      console.log("🏥 Loading ambulance types for hospital user");
      return await apiService.getAmbulanceTypes();
    }
    console.log("👤 Loading ambulance types for regular user");
    return await apiService.getAmbulanceTypes();
  };

  // Helper function to extract ambulance data from response
  const extractAmbulanceData = (response) => {
    if (!response?.data) {
      return null;
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response.data.data)) {
      return response.data.data;
    }
    if (Array.isArray(response.data.ambulance_types)) {
      return response.data.ambulance_types;
    }

    return null;
  };

  // Helper function to handle successful response
  const handleSuccessfulResponse = (response) => {
    console.log("✅ Raw ambulance types response:", response);
    const ambulanceData = extractAmbulanceData(response);

    if (!ambulanceData || ambulanceData.length === 0) {
      console.warn("⚠️ No ambulance types found in response:", response);
      setError("No ambulance types available");
      return;
    }

    console.log("✅ Ambulance types loaded successfully:", ambulanceData.length, "types");
    console.log("✅ Sample ambulance type:", ambulanceData[0]);
    setAllAmbulanceTypes(ambulanceData);
    setAmbulanceTypes(ambulanceData);
  };

  // Helper function to handle error response
  const handleErrorResponse = (error) => {
    console.error("❌ Error loading ambulance types:", error);
    console.error("❌ Error details:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });

    if (error.response?.status === 401) {
      console.warn("🚨 Authentication failed for ambulance types, using fallback data");
      applyFallbackTypes("fallback");
      return;
    }

    console.error("❌ Non-auth error, showing error message to user");
    setError("Failed to load ambulance types");
  };

  // Define fetchEstimateFares function using useCallback to avoid re-creation on every render
  const fetchEstimateFares = useCallback(async () => {
      // For hospital users, skip the type-based endpoint - types will come from Estimate Fare API
      if (isHospitalUser) {
        console.log("🏥 [Hospital User] Skipping type-based endpoint - will use Estimate Fare API response");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(""); // Clear any previous errors

      // Set a safety timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.warn("⏰ Ambulance types loading timeout, using fallback data");
        applyFallbackTypes("timeout-fallback");
        setLoading(false);
      }, 10000); // 10 second timeout

      try {
        const response = await selectAmbulanceTypesAPI();
        
        if (!response?.data) {
          console.warn("⚠️ Invalid response structure:", response);
          setError("Invalid response from server");
          return;
        }

        handleSuccessfulResponse(response);
      } catch (error) {
        handleErrorResponse(error);
      } finally {
        clearTimeout(timeoutId); // Clear the safety timeout
        setLoading(false);
      }
  }, [isFleetUser, isHospitalUser, pickupCoordinates, dropCoordinates]);

  // Load ambulance types and fare estimates from new API
  useEffect(() => {
    fetchEstimateFares();
  }, [fetchEstimateFares]);

  // Load ambulance types when user type changes
  useEffect(() => {
    fetchEstimateFares();
  }, [fetchEstimateFares]);

  // Filter ambulance types based on selected fleet partners (for hospital users)
  // DISABLED: Always show all ambulance types (filtering disabled)
  useEffect(() => {
    const filterAmbulanceTypesByFleet = async () => {
      // For hospital users, don't run this filter - types come from Estimate Fare API
      if (isHospitalUser) {
        console.log("🏥 [HOSPITAL USER] Skipping fleet filter - types managed by Estimate Fare API");
        return;
      }
      
      // Always show all ambulance types - skip filtering
      console.log("🔓 [FLEET FILTER] Showing all ambulance types - filtering disabled");
      setAmbulanceTypes(allAmbulanceTypes);
      setIsFiltered(false);
      setFilterInfo(null);
      return;

      // Skip if hospital partners are selected (different filter)
      if (bookingFormData?.hospitalPartnerIds && bookingFormData.hospitalPartnerIds.length > 0) {
        return;
      }

      // If no fleet partners selected, show all ambulance types
      if (!bookingFormData?.fleetPartnerIds || bookingFormData.fleetPartnerIds.length === 0) {
        console.log("🔓 [FLEET FILTER] No fleet partners selected - showing all ambulance types");
        setAmbulanceTypes(allAmbulanceTypes);
        setIsFiltered(false);
        setFilterInfo(null);
        return;
      }

      // If no ambulance types loaded yet, skip filtering
      if (allAmbulanceTypes.length === 0) {
        console.log("⏳ [FLEET FILTER] Waiting for ambulance types to load...");
        return;
      }

      try {
        const fleetPartnerIds = bookingFormData.fleetPartnerIds;
        console.log("🔍 [FLEET FILTER] ========================================");
        console.log("🔍 [FLEET FILTER] Starting filter process...");
        console.log("🔍 [FLEET FILTER] Selected Fleet Partner IDs:", fleetPartnerIds);
        console.log("🔍 [FLEET FILTER] Total ambulance types available:", allAmbulanceTypes.length);

        setIsFiltered(true);

        // Fetch ambulances for each selected fleet operator
        const ambulancePromises = fleetPartnerIds.map(async (fleetOperatorId, index) => {
          try {
            console.log(`🚛 [FLEET FILTER] Fetching ambulances for fleet ${index + 1}/${fleetPartnerIds.length}: ${fleetOperatorId}`);
            const response = await apiService.getAmbulances({
              operator_id: fleetOperatorId,
              limit: 1000, // Get all ambulances from this fleet
              page: 1,
            });

            // Extract ambulances from response
            let ambulances = [];
            if (response?.data?.ambulances) {
              ambulances = response.data.ambulances;
            } else if (Array.isArray(response?.data)) {
              ambulances = response.data;
            } else if (Array.isArray(response?.ambulances)) {
              ambulances = response.ambulances;
            } else if (Array.isArray(response)) {
              ambulances = response;
            }

            console.log(`✅ [FLEET FILTER] Fleet ${fleetOperatorId}: Found ${ambulances.length} ambulances`);
            return ambulances;
          } catch (error) {
            console.error(`❌ [FLEET FILTER] Error fetching ambulances for fleet ${fleetOperatorId}:`, error);
            return [];
          }
        });

        const ambulanceArrays = await Promise.all(ambulancePromises);
        const allAmbulances = ambulanceArrays.flat();

        console.log(`📦 [FLEET FILTER] Total ambulances fetched: ${allAmbulances.length}`);

        // Extract unique ambulance types from ambulances
        const availableTypeIds = new Set();
        const typeMap = new Map(); // Store type details for debugging

        allAmbulances.forEach((ambulance) => {
          let typeId = null;
          if (ambulance.type_id) {
            typeId = ambulance.type_id;
          } else if (ambulance.type?.type_id) {
            typeId = ambulance.type.type_id;
          }

          if (typeId) {
            availableTypeIds.add(typeId);
            if (!typeMap.has(typeId)) {
              typeMap.set(typeId, {
                typeId,
                typeName: ambulance.type?.name || ambulance.type_id,
                fleetCount: 0
              });
            }
          }
        });

        console.log("📋 [FLEET FILTER] Available ambulance type IDs from fleets:", Array.from(availableTypeIds));
        console.log("📋 [FLEET FILTER] Type details:", Array.from(typeMap.values()));

        // Filter ambulance types to only include those available from selected fleets
        const filteredTypes = allAmbulanceTypes.filter((type) =>
          availableTypeIds.has(type.type_id)
        );

        const filteredTypeNames = filteredTypes.map(t => t.name);
        const excludedTypeNames = allAmbulanceTypes
          .filter(t => !availableTypeIds.has(t.type_id))
          .map(t => t.name);

        console.log("✅ [FLEET FILTER] ========================================");
        console.log(`✅ [FLEET FILTER] Filtered to ${filteredTypes.length} ambulance types from ${allAmbulanceTypes.length} total types`);
        console.log("✅ [FLEET FILTER] Showing types:", filteredTypeNames);
        if (excludedTypeNames.length > 0) {
          console.log("🚫 [FLEET FILTER] Excluded types:", excludedTypeNames);
        }
        console.log("✅ [FLEET FILTER] ========================================");

        // Store filter info for UI display
        setFilterInfo({
          filterType: 'fleet',
          selectedPartners: fleetPartnerIds.length,
          totalTypes: allAmbulanceTypes.length,
          filteredTypes: filteredTypes.length,
          excludedTypes: excludedTypeNames.length
        });

        setAmbulanceTypes(filteredTypes);
      } catch (error) {
        console.error("❌ [FLEET FILTER] Error filtering ambulance types by fleet:", error);
        console.error("❌ [FLEET FILTER] Error details:", error.message, error.stack);
        // On error, show all types instead of none
        setAmbulanceTypes(allAmbulanceTypes);
        setIsFiltered(false);
        setFilterInfo(null);
      }
    };

    filterAmbulanceTypesByFleet();
  }, [bookingFormData?.fleetPartnerIds, bookingFormData?.hospitalPartnerIds, allAmbulanceTypes, isHospitalUser]);

  // Initialize fleet partner names on mount
  useEffect(() => {
    setFleetPartnerNames(['Partner']);
  }, []);

  // Fetch fleet partner names for display
  useEffect(() => {
    const fetchFleetPartnerNames = async () => {
      // If no fleet partners selected, use empty operator map
      if (!bookingFormData?.fleetPartnerIds || bookingFormData.fleetPartnerIds.length === 0) {
        setFleetOperatorMap({});
        setFleetPartnerNames(['Partner']); // Set "Partner" as default partner name for the chip
        return;
      }

      try {
        const fleetPartnerIds = bookingFormData.fleetPartnerIds;
        const operatorMap = {};

        // Fetch fleet details for each partner ID (maintain order)
        const fleetPromises = fleetPartnerIds.map(async (fleetId) => {
          try {
            const response = await apiService.getFleetById(fleetId);
            const fleet = response?.data || response;
            const fleetName = fleet?.name || fleet?.fleet_name || 'Fleet Operator';

            // Map ambulance types to this fleet operator
            // We'll update this when we fetch ambulances
            return { fleetId, fleetName, fleet };
          } catch (error) {
            console.error(`Error fetching fleet ${fleetId}:`, error);
            return { fleetId, fleetName: 'Partner', fleet: null };
          }
        });

        const fleetData = await Promise.all(fleetPromises);
        // Maintain the order of fleetPartnerIds
        const orderedFleetNames = fleetPartnerIds.map(id => {
          const fleet = fleetData.find(f => String(f.fleetId) === String(id));
          return fleet?.fleetName || 'Partner';
        });
        setFleetPartnerNames(orderedFleetNames);

        // Fetch ambulances to map types to fleet operators
        const ambulancePromises = fleetPartnerIds.map(async (fleetId) => {
          try {
            const response = await apiService.getAmbulances({
              operator_id: fleetId,
              limit: 1000,
            });

            let ambulances = [];
            if (response?.data?.ambulances) {
              ambulances = response.data.ambulances;
            } else if (Array.isArray(response?.data)) {
              ambulances = response.data;
            } else if (Array.isArray(response)) {
              ambulances = response;
            }

            const fleetInfo = fleetData.find(f => f.fleetId === fleetId);
            const fleetName = fleetInfo?.fleetName || 'Fleet Operator';

            // Map each ambulance type to the fleet operator
            ambulances.forEach((ambulance) => {
              const typeId = ambulance.type_id || ambulance.type?.type_id;
              if (typeId && !operatorMap[typeId]) {
                operatorMap[typeId] = fleetName;
              }
            });

            return ambulances;
          } catch (error) {
            console.error(`Error fetching ambulances for fleet ${fleetId}:`, error);
            return [];
          }
        });

        await Promise.all(ambulancePromises);
        setFleetOperatorMap(operatorMap);
      } catch (error) {
        console.error('Error fetching fleet partner names:', error);
      }
    };

    fetchFleetPartnerNames();
  }, [bookingFormData?.fleetPartnerIds, ambulanceTypes]);

  // Filter ambulance types based on selected hospital partners (for fleet operators)
  // DISABLED: Always show all ambulance types (filtering disabled)
  useEffect(() => {
    const filterAmbulanceTypesByHospital = async () => {
      // Always show all ambulance types - skip filtering
      console.log("🔓 [HOSPITAL FILTER] Showing all ambulance types - filtering disabled");
      // setAmbulanceTypes(allAmbulanceTypes); // removed: allAmbulanceTypes no longer exists
      setIsFiltered(false);
      setFilterInfo(null);
      return;

      // Skip if fleet partners are selected (different filter)
      if (bookingFormData?.fleetPartnerIds && bookingFormData.fleetPartnerIds.length > 0) {
        return;
      }

      // If no hospital partners selected, show all ambulance types
      if (!bookingFormData?.hospitalPartnerIds || bookingFormData.hospitalPartnerIds.length === 0) {
        console.log("🔓 [HOSPITAL FILTER] No hospital partners selected - showing all ambulance types");
        // setAmbulanceTypes(allAmbulanceTypes); // removed: allAmbulanceTypes no longer exists
        setIsFiltered(false);
        setFilterInfo(null);
        return;
      }

      // If no ambulance types loaded yet, skip filtering
      // if (allAmbulanceTypes.length === 0) {
      //   console.log("⏳ [HOSPITAL FILTER] Waiting for ambulance types to load...");
      //   return;
      // }

      try {
        const hospitalPartnerIds = bookingFormData.hospitalPartnerIds;
        console.log("🏥 [HOSPITAL FILTER] ========================================");
        console.log("🏥 [HOSPITAL FILTER] Starting filter process...");
        console.log("🏥 [HOSPITAL FILTER] Selected Hospital Partner IDs:", hospitalPartnerIds);
        // console.log("🏥 [HOSPITAL FILTER] Total ambulance types available:", allAmbulanceTypes.length);

        setIsFiltered(true);

        // For fleet → hospital filtering:
        // We need to get ambulance types that are commonly used with these hospitals
        // We can fetch bookings associated with these hospitals to see what types they typically use
        // Or we can get ambulance types from bookings where this fleet's ambulances were used with these hospitals

        // Fetch bookings for each selected hospital to see what ambulance types are typically used
        const bookingPromises = hospitalPartnerIds.map(async (hospitalId, index) => {
          try {
            console.log(`🏥 [HOSPITAL FILTER] Fetching bookings for hospital ${index + 1}/${hospitalPartnerIds.length}: ${hospitalId}`);
            // Fetch recent bookings for this hospital to see what ambulance types are used
            const response = await apiService.get(`/bookings/hospital/${hospitalId}?limit=100`);

            let bookings = [];
            if (response?.data?.bookings) {
              bookings = response.data.bookings;
            } else if (Array.isArray(response?.data)) {
              bookings = response.data;
            } else if (Array.isArray(response?.bookings)) {
              bookings = response.bookings;
            } else if (Array.isArray(response)) {
              bookings = response;
            }

            console.log(`✅ [HOSPITAL FILTER] Hospital ${hospitalId}: Found ${bookings.length} bookings`);
            return bookings;
          } catch (error) {
            console.error(`❌ [HOSPITAL FILTER] Error fetching bookings for hospital ${hospitalId}:`, error);
            return [];
          }
        });

        const bookingArrays = await Promise.all(bookingPromises);
        const allBookings = bookingArrays.flat();

        console.log(`📦 [HOSPITAL FILTER] Total bookings fetched: ${allBookings.length}`);

        // Extract unique ambulance type IDs from bookings
        const availableTypeIds = new Set();

        allBookings.forEach((booking) => {
          if (booking.ambulanceType) {
            availableTypeIds.add(booking.ambulanceType);
          } else if (booking.vehicle?.type_id) {
            availableTypeIds.add(booking.vehicle.type_id);
          } else if (booking.vehicle?.type?.type_id) {
            availableTypeIds.add(booking.vehicle.type.type_id);
          }
        });

        // If we have type IDs, filter by them
        // Otherwise, if no bookings found, show all types (fallback)
        let filteredTypes;
        if (availableTypeIds.size > 0) {
          // filteredTypes = allAmbulanceTypes.filter((type) =>
          //   availableTypeIds.has(type.type_id)
          // );
        } else {
          // No bookings found with these hospitals, show all types as fallback
          console.log("⚠️ [HOSPITAL FILTER] No bookings found with selected hospitals - showing all types");
          // filteredTypes = allAmbulanceTypes;
        }

        const filteredTypeNames = filteredTypes.map(t => t.name);
        // const excludedTypeNames = allAmbulanceTypes
        //   .filter(t => !availableTypeIds.has(t.type_id))
        //   .map(t => t.name);

        console.log("✅ [HOSPITAL FILTER] ========================================");
        // console.log(`✅ [HOSPITAL FILTER] Filtered to ${filteredTypes.length} ambulance types from ${allAmbulanceTypes.length} total types`);
        console.log("✅ [HOSPITAL FILTER] Available type IDs from bookings:", Array.from(availableTypeIds));
        console.log("✅ [HOSPITAL FILTER] Showing types:", filteredTypeNames);
        if (excludedTypeNames.length > 0 && availableTypeIds.size > 0) {
          console.log("🚫 [HOSPITAL FILTER] Excluded types:", excludedTypeNames);
        }
        console.log("✅ [HOSPITAL FILTER] ========================================");

        // Store filter info for UI display
        setFilterInfo({
          filterType: 'hospital',
          selectedPartners: hospitalPartnerIds.length,
          // totalTypes: allAmbulanceTypes.length,
          filteredTypes: filteredTypes.length,
          excludedTypes: availableTypeIds.size > 0 ? excludedTypeNames.length : 0
        });

        setAmbulanceTypes(filteredTypes);
      } catch (error) {
        console.error("❌ [HOSPITAL FILTER] Error filtering ambulance types by hospital:", error);
        console.error("❌ [HOSPITAL FILTER] Error details:", error.message, error.stack);
        // On error, show all types instead of none
        // setAmbulanceTypes(allAmbulanceTypes);
        setIsFiltered(false);
        setFilterInfo(null);
      }
    };

    filterAmbulanceTypesByHospital();
  }, [bookingFormData?.hospitalPartnerIds, bookingFormData?.fleetPartnerIds]);

  // Helper function to initialize loading state
  const initializeLoadingState = () => {
    const loadingState = {};
    ambulanceTypes.forEach(type => {
      loadingState[type.type_id] = true;
    });
    setFareLoading(loadingState);
  };

  // Helper function to select fare estimation API based on user role
  const selectFareEstimationAPI = async (pickup, drop) => {
    if (isFleetUser) {
      console.log("🚛 Using fleet estimate fare API for fleet user");
      return await authService.fleetEstimateBookingFare({
        pickup_latitude: pickup.latitude,
        pickup_longitude: pickup.longitude,
        dropoff_latitude: drop.latitude,
        dropoff_longitude: drop.longitude
      });
    }
    if (isHospitalUser) {
      console.log("🏥 Using hospital estimate fare API (V2) for hospital user");
      const hospitalId = user?.hospital_id ?? user?.hospitalId ?? user?.hospital?.hospital_id ?? user?.hospital?.id;
      return await hospitalService.estimateFare(pickup, drop, { hospitalId });
    }
    console.log("👤 Using regular booking estimate fare API for regular user");
    return await bookingService.estimateFare(pickup, drop);
  };

  // Helper function to extract distance from response
  const extractDistance = (responseData) => {
    const backendDistance = responseData.distance_km ||
                          responseData.distance ||
                          responseData.distanceKm;

    if (backendDistance && backendDistance > 0) {
      console.log("📏 Using backend distance:", backendDistance, "km");
      setCalculatedDistance(Math.round(backendDistance * 10) / 10);
    }
  };

  // Helper function to process fleet batch estimates
  const processFleetBatchEstimates = (estimates, newEstimates) => {
    console.log("Processing fleet batch estimates:", estimates.length, "estimates");
    estimates.forEach(estimate => {
      const typeId = estimate.type_id;
      const fare = estimate.estimated_total;
      if (typeId && fare) {
        newEstimates[typeId] = fare;
      }
    });
  };

  // Helper function to process fleet single estimate
  const processFleetSingleEstimate = (responseData, newEstimates) => {
    console.log("Processing fleet single estimate for type:", responseData.ambulance_type);
    const matchingType = ambulanceTypes.find(type =>
      type.name === responseData.ambulance_type ||
      type.type_id === responseData.ambulance_type
    );
    if (matchingType) {
      newEstimates[matchingType.type_id] = responseData.estimated_total;
    }
  };

  // Helper function to process fleet estimates
  const processFleetEstimates = (responseData, newEstimates) => {
    if (responseData.estimates && Array.isArray(responseData.estimates)) {
      processFleetBatchEstimates(responseData.estimates, newEstimates);
      return;
    }
    if (responseData.estimated_total && responseData.ambulance_type) {
      processFleetSingleEstimate(responseData, newEstimates);
      return;
    }
    console.warn("Unknown fleet estimate response format:", responseData);
  };

  // Helper function to update estimate with vendor preference logic
  const updateEstimateWithPreference = (newEstimates, vendorNames, typeId, fare, fleet) => {
    if (!newEstimates[typeId]) {
      newEstimates[typeId] = fare;
      if (fleet.operator_name) {
        vendorNames[typeId] = fleet.operator_name;
      }
      return;
    }

    const shouldUpdate = (fleet.is_partner && fare < newEstimates[typeId]) ||
                        (!fleet.is_partner && newEstimates[typeId] > fare);

    if (shouldUpdate) {
      newEstimates[typeId] = fare;
      if (fleet.operator_name) {
        vendorNames[typeId] = fleet.operator_name;
      }
    }
  };

  // Helper function to extract ambulance types from estimate fare response
  // Returns ALL types from ALL operators (no deduplication)
  const extractAmbulanceTypesFromResponse = (responseData) => {
    const extractedTypes = [];

    // Handle fleet estimate response: flat estimates array
    if (responseData.estimates && Array.isArray(responseData.estimates)) {
      responseData.estimates.forEach(estimate => {
        const typeId = estimate.type_id;
        if (typeId) {
          extractedTypes.push({
            type_id: typeId,
            unique_key: typeId,
            name: estimate.name || estimate.type_name || estimate.ambulance_type || `Ambulance Type ${typeId.slice(-4)}`,
            description: estimate.description || `${estimate.ambulance_type || 'Ambulance'} service`,
            base_fare: estimate.base_fare || 0,
            per_km_rate: estimate.per_km_rate || 0,
            estimated_fare: estimate.estimated_total ?? estimate.total ?? estimate.estimated_fare ?? null,
            breakdown: estimate.breakdown || null,
            pricing_source: estimate.pricing_source ?? estimate.pricingSource ?? null,
            model: estimate.model ?? null,
          });
        }
      });
    }

    // Handle hospital fleet response: nested fleets array
    if (responseData.fleets && Array.isArray(responseData.fleets)) {
      responseData.fleets.forEach(fleet => {
        if (fleet.estimates && Array.isArray(fleet.estimates)) {
          fleet.estimates.forEach(estimate => {
            const typeId = estimate.type_id;
            const operatorId = fleet.operator_id;
            
            if (typeId) {
              // Create a unique key combining type_id and operator_id
              const uniqueKey = `${typeId}_${operatorId}`;
              
              const ambulanceType = {
                type_id: typeId,
                operator_id: operatorId,
                operator_name: fleet.operator_name || 'Unknown Operator',
                is_partner: fleet.is_partner || false,
                unique_key: uniqueKey, // For unique identification
                name: estimate.name || estimate.type_name || estimate.ambulance_type || `Ambulance Type ${typeId.slice(-4)}`,
                description: estimate.description || `${estimate.name || 'Ambulance'} service`,
                base_fare: estimate.base_fare || 0,
                per_km_rate: estimate.per_km_rate || 0,
                estimated_fare: estimate.total ?? estimate.estimated_fare ?? null,
                breakdown: estimate.breakdown || null,
                pricing_source: estimate.pricing_source ?? estimate.pricingSource ?? null,
                model: estimate.model ?? null,
              };
              
              extractedTypes.push(ambulanceType);
            }
          });
        }
      });
    }

    // Also check if ambulance_types is directly in responseData
    if (responseData.ambulance_types && Array.isArray(responseData.ambulance_types)) {
      responseData.ambulance_types.forEach(type => {
        const typeId = type.type_id || type.id;
        if (typeId) {
          extractedTypes.push({
            ...type,
            unique_key: typeId // Use type_id as unique key if no operator info
          });
        }
      });
    }

    return extractedTypes;
  };

  // Helper function to process hospital fleet estimates
  const processHospitalFleetEstimates = (fleets, newEstimates) => {
    console.log("Processing hospital fleet estimates:", fleets.length, "fleets");
    
    const partnerTypeIdsSet = new Set();
    const vendorNames = {};
    
    fleets.forEach(fleet => {
      if (!fleet.estimates || !Array.isArray(fleet.estimates)) {
        return;
      }

      fleet.estimates.forEach(estimate => {
        const typeId = estimate.type_id;
        const operatorId = fleet.operator_id;
        const fare = estimate.total ?? estimate.estimated_fare;
        
        // Create unique key for this type + operator combination
        const uniqueKey = `${typeId}_${operatorId}`;

        if (fleet.is_partner === true) {
          partnerTypeIdsSet.add(typeId);
        }

        // Store estimate by unique key for hospital users (to show all operators)
        if (isHospitalUser) {
          newEstimates[uniqueKey] = fare;
          if (fleet.operator_name) {
            vendorNames[uniqueKey] = fleet.operator_name;
          }
        } else {
          // For non-hospital users, use the preference logic
          updateEstimateWithPreference(newEstimates, vendorNames, typeId, fare, fleet);
        }
      });
    });
    
    setPartnerTypeIds(partnerTypeIdsSet);
    setVendorNamesMap(vendorNames);
    console.log("Partner type IDs from estimate fare API:", Array.from(partnerTypeIdsSet));
    console.log("Vendor names from estimate fare API:", vendorNames);
  };

  // Helper function to process fallback single estimate
  const processFallbackEstimate = (responseData, newEstimates) => {
    const typeId = ambulanceTypes[0]?.type_id;
    if (typeId) {
      newEstimates[typeId] = responseData.estimated_total;
    }
  };

  // Helper function to process response data
  const processFareResponse = (response) => {
    if (!response.success || !response.data) {
      return;
    }

    const newEstimates = {};
    console.log("Raw fare estimation response:", response.data);

    extractDistance(response.data);

    // For hospital users, extract ambulance types from estimate fare response
    if (isHospitalUser && response.data) {
      const extractedTypes = extractAmbulanceTypesFromResponse(response.data);
      if (extractedTypes.length > 0) {
        console.log("🏥 Extracted ambulance types from estimate fare API:", extractedTypes.length, "types");
        console.log("🏥 Ambulance types:", extractedTypes);
        console.log("🏥 All type IDs:", extractedTypes.map(t => t.type_id));
        
        // Store fare estimates by unique key for each type
        extractedTypes.forEach(type => {
          if (type.estimated_fare !== null && type.estimated_fare !== undefined) {
            const fareKey = type.unique_key || type.type_id;
            newEstimates[fareKey] = type.estimated_fare;
          }
        });
        
        // Update ambulance types state with types from estimate fare response
        // Set both allAmbulanceTypes and ambulanceTypes to ensure all are displayed
        setAllAmbulanceTypes(extractedTypes);
        setAmbulanceTypes(extractedTypes);
        console.log("🏥 Updated ambulanceTypes state with", extractedTypes.length, "types");
        console.log("🏥 Fare estimates stored:", Object.keys(newEstimates).length, "estimates");
      } else {
        console.warn("🏥 No ambulance types extracted from estimate fare response");
      }
    }

    if (isFleetUser) {
      // Extract full ambulance type objects (including breakdown/pricing_source/model)
      // from the fleet estimate response, same as hospital flow
      const extractedTypes = extractAmbulanceTypesFromResponse(response.data);
      if (extractedTypes.length > 0) {
        extractedTypes.forEach(type => {
          if (type.estimated_fare !== null && type.estimated_fare !== undefined) {
            newEstimates[type.unique_key || type.type_id] = type.estimated_fare;
          }
        });
        setAllAmbulanceTypes(extractedTypes);
        setAmbulanceTypes(extractedTypes);
      } else {
        // Fallback: use old path that only stores estimated_total
        processFleetEstimates(response.data, newEstimates);
      }
    } else if (response.data.fleets && Array.isArray(response.data.fleets)) {
      processHospitalFleetEstimates(response.data.fleets, newEstimates);
    } else if (response.data.estimated_total) {
      processFallbackEstimate(response.data, newEstimates);
    }

    console.log("Processed fare estimates:", newEstimates);
    console.log("Current ambulance types:", ambulanceTypes.map(t => ({ id: t.type_id, name: t.name })));

    setFareEstimates(newEstimates);
    if (onFareEstimatesChange) {
      onFareEstimatesChange(newEstimates);
    }
  };

  // Debounced fare calculation function
  const debouncedCalculateFares = useCallback(
    debounce(async (pickup, drop) => {
      if (!pickup || !drop) return;

      console.log("Debounced fare calculation triggered");

      setPartnerTypeIds(new Set());
      initializeLoadingState();

      try {
        const response = await selectFareEstimationAPI(pickup, drop);
        processFareResponse(response);
      } catch (error) {
        console.error("Error calculating fares:", error);
      } finally {
        setFareLoading({});
      }
    }, 500), // 500ms debounce
    [onFareEstimatesChange, ambulanceTypes, isFleetUser, isHospitalUser]
  );

  // ❌ REMOVED: Frontend distance calculation - Backend handles everything

  // Calculate fares when coordinates change (debounced)
  useEffect(() => {
    console.log("🚗 Fare calculation effect triggered:", {
      hasPickup: !!pickupCoordinates,
      hasDropoff: !!dropCoordinates,
      pickup: pickupCoordinates,
      dropoff: dropCoordinates,
      currentKey: fareCalculationKeyRef.current
    });

    if (pickupCoordinates && dropCoordinates) {
      // Create a unique key for this calculation to prevent duplicate calls
      const calculationKey = `${pickupCoordinates.latitude}-${pickupCoordinates.longitude}-${dropCoordinates.latitude}-${dropCoordinates.longitude}`;

      console.log("🚗 Coordinates available, checking calculation key:", {
        newKey: calculationKey,
        currentKey: fareCalculationKeyRef.current,
        needsCalculation: fareCalculationKeyRef.current !== calculationKey
      });

      if (fareCalculationKeyRef.current !== calculationKey) {
        console.log("🚗 Triggering fare calculation for new coordinates");
        fareCalculationKeyRef.current = calculationKey;
        debouncedCalculateFares(
          pickupCoordinates,
          dropCoordinates
        );
      } else {
        console.log("🚗 Skipping fare calculation - same coordinates as before");
      }
    } else {
      console.log("🚗 Missing coordinates, cannot calculate fares:", {
        hasPickup: !!pickupCoordinates,
        hasDropoff: !!dropCoordinates
      });
    }
  }, [
    pickupCoordinates,
    dropCoordinates,
    debouncedCalculateFares,
  ]);

  // Handle ambulance selection - just select, don't auto-confirm
  const handleAmbulanceSelect = (ambulance) => {
    console.log("=== AMBULANCE SELECTION DEBUG ===");
    console.log("Selected ambulance:", ambulance);
    console.log("Ambulance type_id:", ambulance.type_id);
    console.log("Ambulance name:", ambulance.name);
    console.log("Current selectedAmbulance:", selectedAmbulance);

    // Just select the ambulance, user will click "Book Ambulance" button
    if (onAmbulanceSelect) {
      onAmbulanceSelect(ambulance);
    }
  };

  // Calculate estimated fare using backend API
  const calculateEstimatedFare = async (
    ambulanceType,
    pickupCoords,
    dropCoords
  ) => {
    // This function is now mainly used for fallback or individual checks if needed,
    // but the main logic is handled by debouncedCalculateFares updating the state.
    // We can return the value from state if available.

    if (ambulanceType?.type_id && fareEstimates[ambulanceType.type_id]) {
      return fareEstimates[ambulanceType.type_id];
    }

    // Fallback to frontend calculation
    const distance =
      pickupCoords && dropCoords
        ? calculateDistance(
          pickupCoords.latitude,
          pickupCoords.longitude,
          dropCoords.latitude,
          dropCoords.longitude
        )
        : 25; // Fallback distance if no coordinates available

    const baseFare = (ambulanceType?.base_fare || 0) * 0.7; // Reduced base fare
    const distanceFare = (ambulanceType?.per_km_rate || 0) * 0.8 * distance; // Reduced per km rate
    return Math.round(baseFare + distanceFare);
  };

  // ❌ REMOVED: Frontend distance calculation function - Backend handles all distance calculations
  // Get the primary fleet partner name for the chip (preferred or first)
  const getPrimaryFleetPartnerName = () => {
    // Return the first fleet partner name from the response
    if (fleetPartnerNames && fleetPartnerNames.length > 0) {
      return fleetPartnerNames[0];
    }
    
    // Fallback: get from selected ambulance's operator_name
    if (selectedAmbulance?.operator_name) {
      return selectedAmbulance.operator_name;
    }

    // Fallback: get from first ambulance type
    if (ambulanceTypes.length > 0 && ambulanceTypes[0]?.operator_name) {
      return ambulanceTypes[0].operator_name;
    }

    return null;
  };

  const primaryFleetPartnerName = getPrimaryFleetPartnerName();

  // Debug: Log chip name for troubleshooting
  useEffect(() => {
    if (primaryFleetPartnerName) {
      console.log("🏷️ [CHIP] Displaying partner name:", primaryFleetPartnerName, "from fleets:", fleetPartnerNames);
    } else {
      console.log("🏷️ [CHIP] No partner name to display", {
        fleetPartnerNames,
        ambulanceTypesCount: ambulanceTypes.length,
        selectedAmbulance: selectedAmbulance?.name
      });
    }
  }, [primaryFleetPartnerName, fleetPartnerNames, selectedAmbulance, ambulanceTypes]);

  // Helper function to get journey direction display text
  const getJourneyDirectionText = (direction) => {
    if (direction === "INBOUND") return "🔄 Inbound";
    return "➡️ Outbound";
  };

  // Helper function to get filter status colors
  const getFilterStatusColors = (filterType) => {
    if (filterType === 'hospital') {
      return {
        container: 'bg-green-50 border border-green-200',
        dot: 'bg-green-600',
        text: 'text-green-800',
        description: 'text-green-600'
      };
    }
    return {
      container: 'bg-blue-50 border border-blue-200',
      dot: 'bg-blue-600',
      text: 'text-blue-800',
      description: 'text-blue-600'
    };
  };

  // Helper function to get filter status text
  const getFilterStatusText = (filterInfo) => {
    if (filterInfo.filterType === 'hospital') {
      return {
        title: 'Filtered by Hospital Partners',
        partnerType: 'hospital(s)'
      };
    }
    return {
      title: 'Filtered by Fleet Partners',
      partnerType: 'fleet(s)'
    };
  };

  // Helper function to render filter status indicator
  const renderFilterStatusIndicator = () => {
    if (!isFiltered || !filterInfo) return null;

    const colors = getFilterStatusColors(filterInfo.filterType);
    const text = getFilterStatusText(filterInfo);

    return (
      <div className={`mb-3 p-2 rounded-lg ${colors.container}`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full animate-pulse ${colors.dot}`}></div>
          <span className={`text-xs font-semibold ${colors.text}`}>
            {text.title}
          </span>
        </div>
        <div className={`text-xs mt-1 ${colors.description}`}>
          Showing {filterInfo.filteredTypes} of {filterInfo.totalTypes} types from {filterInfo.selectedPartners} selected {text.partnerType}
        </div>
        {process.env.NODE_ENV === 'development' && (
          <div className="text-xs text-gray-500 mt-1 font-mono">
            [DEBUG] Check console for details
          </div>
        )}
      </div>
    );
  };

  // Helper function to render loading state
  const renderLoadingState = () => (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      <span className="ml-2 text-gray-600">Loading ambulances...</span>
    </div>
  );

  // Helper function to render error state
  const renderErrorState = () => (
    <div className="flex items-center justify-center py-8">
      <div className="text-red-600 text-sm">{error}</div>
    </div>
  );

  // Helper function to render empty state
  const renderEmptyState = () => (
    <div className="flex items-center justify-center py-8">
      <div className="text-center text-gray-500">
        <p className="text-sm">No ambulances available</p>
        <p className="text-xs mt-1">Please check back later</p>
      </div>
    </div>
  );

  // Helper function to get ambulance card className
  const getAmbulanceCardClassName = (isSelected) => {
    if (isSelected) {
      return "relative flex items-start gap-3 border rounded-lg p-2.5 cursor-pointer transition-all overflow-hidden border-blue-500 bg-blue-50";
    }
    return "relative flex items-start gap-3 border rounded-lg p-2.5 cursor-pointer transition-all overflow-hidden border-gray-200 hover:bg-gray-50";
  };

  // Helper function to get ambulance name className
  const getAmbulanceNameClassName = (isSelected) => {
    return `font-semibold text-sm leading-tight break-words ${isSelected ? "text-blue-900" : "text-gray-900"}`;
  };

  // Helper function to get fare display className
  const getFareDisplayClassName = (isSelected) => {
    return `font-bold text-sm whitespace-nowrap ${isSelected ? "text-blue-900" : "text-gray-900"}`;
  };

  // Helper function to render fare display
  const renderFareDisplay = (isLoadingFare, estimatedFare) => {
    if (isLoadingFare) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    if (estimatedFare) {
      return `₹${Math.round(estimatedFare)}`;
    }
    return "₹--";
  };

  // Helper function to render ambulance card
  const renderAmbulanceCard = (ambulance, index) => {
    // Use unique_key for selection if available, otherwise fall back to type_id
    const selectionKey = ambulance.unique_key || ambulance.type_id;
    const isSelected = selectedAmbulance?.unique_key === selectionKey || 
                      (selectedAmbulance?.type_id === ambulance.type_id && !selectedAmbulance?.unique_key);
    
    // For fare estimates, try unique key first, then type_id
    const fareKey = ambulance.unique_key || ambulance.type_id;
    const apiEstimate = fareEstimates[fareKey] || fareEstimates[ambulance.type_id];
    const estimatedFare = apiEstimate || ambulance.estimated_fare || null;
    const isLoadingFare = fareLoading[fareKey] || fareLoading[ambulance.type_id];
    
    // Get operator name from ambulance object or vendorNamesMap
    const vendorName = ambulance.operator_name || vendorNamesMap[ambulance.type_id];
    
    // Check if this is a partner
    // For hospital users: use is_partner directly from estimate fare response
    // For other users: fallback to partnerTypeIds check
    const isPartner = isHospitalUser 
      ? (ambulance.is_partner === true)  // Strict check: only show if explicitly true
      : (ambulance.is_partner === true || partnerTypeIds.has(ambulance.type_id));

    // Debug: Log backend fare estimate only
    if (ambulance.name === "Advanced Life Support" && renderCountRef.current % 20 === 0) {
      console.log(`💰 Backend fare for ${ambulance.name}:`, {
        typeId: ambulance.type_id,
        backendEstimate: apiEstimate,
        finalFare: estimatedFare,
        isLoading: isLoadingFare,
        allBackendEstimates: Object.keys(fareEstimates)
      });
    }

    return (
      <div
        key={ambulance.unique_key || `${ambulance.type_id}_${ambulance.operator_id || index}`}
        className={getAmbulanceCardClassName(isSelected)}
        onClick={() => handleAmbulanceSelect(ambulance)}
      >
        {/* Ambulance Icon */}
        <div className="flex-shrink-0">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center overflow-hidden">
            <img
              src={ambulanceImages[index % ambulanceImages.length]}
              alt={ambulance.name}
              className="w-full h-full object-contain"
            />
          </div>
        </div>
        {/* Ambulance Details */}
        <div className="flex-1 flex items-start justify-between gap-2 min-w-0 overflow-hidden">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2">
              <p className={getAmbulanceNameClassName(isSelected)} title={ambulance.name}>
                {ambulance.abbreviation || getAmbulanceTypeAbbreviation(ambulance.name) || ambulance.name}
              </p>
              {isPartner && (
                <span className="inline-flex items-center bg-green-50 border border-green-200 rounded-full px-2 py-0.5 text-xs font-medium text-green-700 whitespace-nowrap">
                  Partner
                </span>
              )}
            </div>
            {vendorName && (
              <p className="text-xs text-blue-600 font-medium mt-0.5 leading-relaxed break-words">
                {vendorName}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed break-words">
              Professional ambulance service with trained medical staff.
            </p>
          </div>
          <div className="text-right flex-shrink-0 ml-2">
            <div className={getFareDisplayClassName(isSelected)}>
              {renderFareDisplay(isLoadingFare, estimatedFare)}
            </div>
            <div className="text-xs text-gray-500 whitespace-nowrap">Est. Fare</div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPricingInfoData({
                  pricing_source: ambulance.pricing_source ?? null,
                  pricing_model: ambulance.model ?? null,
                  pricing_breakdown: ambulance.breakdown ?? null,
                });
              }}
              className="mt-1 p-0.5 rounded-full text-gray-400 hover:text-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
              title="Pricing breakdown"
              aria-label="Pricing breakdown"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </button>
            {isSelected && (
              <div className="text-xs text-blue-600 font-medium mt-1 whitespace-nowrap">
                Selected
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render ambulance list content
  const renderAmbulanceListContent = () => {
    if (loading) {
      return renderLoadingState();
    }
    if (error) {
      return renderErrorState();
    }
    if (ambulanceTypes.length === 0) {
      return renderEmptyState();
    }

    // Debug: Log how many ambulance types are being rendered
    if (isHospitalUser && ambulanceTypes.length > 0) {
      console.log("🏥 [RENDER] Rendering", ambulanceTypes.length, "ambulance types in UI");
      console.log("🏥 [RENDER] Type IDs:", ambulanceTypes.map(t => t.type_id));
    }

    return (
      <div className="space-y-4">
        {ambulanceTypes.map((ambulance, i) => renderAmbulanceCard(ambulance, i))}
      </div>
    );
  };

  return (
    <>
    <div className="bg-white rounded-2xl shadow-lg p-3 w-full max-w-sm self-stretch flex flex-col relative min-h-[400px] max-h-[90vh]">
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <h2 className="text-base font-semibold">Choose An Ambulance</h2>
        <div className="text-right">
          <span className="text-sm text-gray-500">
            {calculatedDistance ? `${calculatedDistance} km` : "Loading distance..."}
          </span>
          {journeyDirection && (
            <div className="text-xs text-blue-600 font-medium">
              {getJourneyDirectionText(journeyDirection)}
            </div>
          )}
        </div>
      </div>

      <div className="flex-shrink-0">
        {renderFilterStatusIndicator()}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {renderAmbulanceListContent()}
      </div>

      {/* Payment Type Selection */}
      <div className="mt-4 flex-shrink-0">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Payment Method
        </label>
        <div className="relative" ref={paymentDropdownRef}>
          <button
            type="button"
            onClick={() => setIsPaymentDropdownOpen(!isPaymentDropdownOpen)}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-gray-50 hover:bg-white text-left flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              {paymentType === "Cash" && "💵 Cash"}
              {paymentType === "Online" && "💳 Online"}
            </span>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isPaymentDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isPaymentDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
              <PaymentDropdownButton
                value="Cash"
                label="Cash"
                icon="💵"
                currentPaymentType={paymentType}
                onSelect={(value) => {
                  setPaymentType(value);
                }}
                onClose={() => setIsPaymentDropdownOpen(false)}
              />
              <PaymentDropdownButton
                value="Online"
                label="Online"
                icon="💳"
                currentPaymentType={paymentType}
                onSelect={(value) => {
                  setPaymentType(value);
                }}
                onClose={() => setIsPaymentDropdownOpen(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Book Ambulance Button */}
      <button
        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2 shadow-lg hover:shadow-xl transition-colors duration-200 transform hover:scale-[1.02] flex-shrink-0"
        onClick={() => {
          if (!selectedAmbulance) return;
          if (!bookingFormData) return;
          
          // Update bookingFormData with current payment type
          const updatedFormData = {
            ...bookingFormData,
            paymentType: paymentType,
          };
          
          // Call onConfirmSelection with updated form data including payment type
          if (onConfirmSelection) {
            onConfirmSelection(selectedAmbulance, updatedFormData);
          }
        }}
        disabled={isBookingDisabled(false, bookingFormData, selectedAmbulance, pickupCoordinates, dropCoordinates)}
      >
        {selectedAmbulance
          ? `Book ${selectedAmbulance.name}`
          : "Book Ambulance"}
      </button>
    </div>

    {/* Pricing breakdown overlay */}
    {pricingInfoData && (() => {
      const pb = pricingInfoData.pricing_breakdown || {};
      const num = (v) => (v != null && v !== '' && typeof v === 'number' && !Number.isNaN(v) ? `₹${v}` : '—');
      const formatAdjustment = (adj) => {
        if (adj == null || adj === '') return '—';
        if (typeof adj === 'object') {
          const amountNum = Number(adj.amount);
          const amt = !Number.isNaN(amountNum) ? `₹${amountNum}` : null;
          const type = adj.type ?? '';
          const pct = adj.percent != null && adj.percent !== '' ? `${adj.percent}%` : '';
          const parts = [type, pct].filter(Boolean).join(' ');
          if (amt) return parts ? `${parts} • ${amt}` : amt;
          return parts || '—';
        }
        if (typeof adj === 'number' && !Number.isNaN(adj)) return `₹${adj}`;
        return '—';
      };
      const toNum = (v) => (v != null && v !== '' && typeof v === 'number' && !Number.isNaN(v) ? v : 0);
      const adjustmentSigned = (adj) => {
        if (adj == null || adj === '') return 0;
        const amt = typeof adj === 'object' && adj.amount != null ? toNum(adj.amount) : (typeof adj === 'number' ? adj : 0);
        if (amt === 0) return 0;
        const type = (adj && typeof adj === 'object' && (adj.type ?? adj.adjustmentType ?? '')).toString().toUpperCase();
        return type === 'DISCOUNT' ? -amt : amt;
      };
      const parts = [
        toNum(pb.base_fare ?? pb.baseFare),
        toNum(pb.distance_fare ?? pb.distanceFare),
        toNum(pb.time_fare ?? pb.timeFare),
        toNum(pb.driver_charges ?? pb.driverCharges),
        toNum(pb.taxes),
        toNum(pb.commission),
        adjustmentSigned(pb.adjustment),
      ];
      const sum = parts.reduce((a, b) => a + b, 0);
      const decimal = sum % 1;
      const total = decimal <= 0.4 ? Math.floor(sum) : Math.ceil(sum);
      const nonZeroParts = parts.filter((v) => v !== 0);
      const totalFormula = nonZeroParts.reduce((acc, v, idx) => {
        const abs = Math.abs(v);
        if (idx === 0) return `${abs}`;
        return v < 0 ? `${acc} - ${abs}` : `${acc} + ${abs}`;
      }, '');

      return createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
          onClick={() => setPricingInfoData(null)}
          role="dialog"
          aria-label="Pricing breakdown"
        >
          <div
            className="w-full max-w-sm rounded-xl border border-gray-500 bg-gray-900 shadow-2xl p-4 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-semibold text-white mb-3 border-b border-gray-600 pb-2">Pricing breakdown</div>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex justify-between gap-4"><span className="text-gray-400">Source</span><span className="font-medium text-gray-200">{pricingInfoData.pricing_source ?? '—'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Model</span><span className="font-medium text-gray-200">{pricingInfoData.pricing_model ?? '—'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Base fare</span><span>{num(pb.base_fare ?? pb.baseFare)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Distance fare</span><span>{num(pb.distance_fare ?? pb.distanceFare)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Time fare</span><span>{num(pb.time_fare ?? pb.timeFare)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Driver charges</span><span>{num(pb.driver_charges ?? pb.driverCharges)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Taxes</span><span>{num(pb.taxes)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Commission</span><span>{num(pb.commission)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Adjustment</span><span>{formatAdjustment(pb.adjustment)}</span></div>
              <div className="flex justify-between gap-4 pt-2 mt-2 border-t border-gray-600">
                <span className="text-gray-300 font-medium">
                  Total{totalFormula ? ` (${totalFormula})` : ''}
                </span>
                <span className="font-semibold text-white">₹{total}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPricingInfoData(null)}
              className="mt-3 w-full py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      );
    })()}
    </>
  );
};

export default ChooseAmbulance;
